import { createAuthedClient } from "@/api/entities";

const API = "/gold-api";

// The Gold-Hunter backend is now behind the same operator gate as Orchestra, so
// these calls must carry the operator's Google session and second-factor token.
// Named `axios` so the call sites below read unchanged, but it is a scoped
// instance — never the global axios, whose defaults would leak onto every other
// request in the app.
const axios = createAuthedClient("");

function makeEntity(endpoint) {
  return {
    list: async (params) => {
      const res = await axios.get(`${API}/${endpoint}`, { params });
      return res.data;
    },
    create: async (data) => {
      const res = await axios.post(`${API}/${endpoint}`, data);
      return res.data;
    },
    update: async (id, data) => {
      const res = await axios.put(`${API}/${endpoint}/${id}`, data);
      return res.data;
    },
    delete: async (id) => {
      const res = await axios.delete(`${API}/${endpoint}/${id}`);
      return res.data;
    },
    filter: async (params) => {
      const res = await axios.get(`${API}/${endpoint}`, { params });
      return res.data;
    },
  };
}

export const Signal = makeEntity("signals");
export const Lead = makeEntity("leads");
export const DigitalAsset = makeEntity("digital-assets");
export const Timeline = makeEntity("timelines");
export const GoldFinding = makeEntity("gold-findings");
export const VaultEntry = makeEntity("vault");

export async function getVaultSummary() {
  const res = await axios.get(`${API}/vault/summary`);
  return res.data;
}

export async function getVaultAudit() {
  const res = await axios.get(`${API}/vault/audit`);
  return res.data;
}

export async function getRealBalance() {
  const res = await axios.get(`${API}/treasury/real-balance`);
  return res.data;
}

export async function getFreeStack() {
  const res = await axios.get(`${API}/free-stack`);
  return res.data;
}

export async function triggerFreeChannels() {
  const res = await axios.post(`${API}/free-stack/run-channels`);
  return res.data;
}

export async function triggerFreeRescore() {
  const res = await axios.post(`${API}/free-stack/run-rescore`);
  return res.data;
}

export async function getSwarmStatus() {
  const res = await axios.get(`${API}/swarm/status`);
  return res.data;
}

export async function startSwarm() {
  const res = await axios.post(`${API}/swarm/start`);
  return res.data;
}

export async function stopSwarm() {
  const res = await axios.post(`${API}/swarm/stop`);
  return res.data;
}

export async function runSwarmOnce() {
  const res = await axios.post(`${API}/swarm/run-once`);
  return res.data;
}

export async function runAgent(role) {
  const res = await axios.post(`${API}/swarm/run-agent/${role}`);
  return res.data;
}

export async function getDashboardData() {
  const res = await axios.get(`${API}/dashboard`);
  return res.data;
}

export async function getTreasuryStatus() {
  const res = await axios.get(`${API}/treasury/status`);
  return res.data;
}

export async function startTreasury() {
  const res = await axios.post(`${API}/treasury/start`);
  return res.data;
}

export async function stopTreasury() {
  const res = await axios.post(`${API}/treasury/stop`);
  return res.data;
}

export async function distributeNow() {
  const res = await axios.post(`${API}/treasury/distribute-now`);
  return res.data;
}

export async function getPayoutProposals(params) {
  const res = await axios.get(`${API}/treasury/proposals`, { params });
  return res.data;
}

export async function approvePayoutProposal(proposalId) {
  const res = await axios.post(`${API}/treasury/proposals/${proposalId}/approve`);
  return res.data;
}

export async function rejectPayoutProposal(proposalId, reason = "") {
  const res = await axios.post(`${API}/treasury/proposals/${proposalId}/reject`, { reason });
  return res.data;
}

export async function resetCircuitBreaker() {
  const res = await axios.post(`${API}/treasury/reset-circuit-breaker`);
  return res.data;
}

export async function resetGasHalt() {
  const res = await axios.post(`${API}/treasury/reset-gas-halt`);
  return res.data;
}

export async function getTreasuryHistory() {
  const res = await axios.get(`${API}/treasury/history`);
  return res.data;
}

export async function getWatchdogStatus() {
  const res = await axios.get(`${API}/watchdog/status`);
  return res.data;
}

export async function triggerWatchdog() {
  const res = await axios.post(`${API}/watchdog/check`);
  return res.data;
}

// Stripe Payments
export async function createCheckout(packageId, leadId) {
  const originUrl = window.location.origin;
  const res = await axios.post(`${API}/payments/checkout`, {
    package_id: packageId,
    lead_id: leadId || "",
    origin_url: originUrl,
  });
  return res.data;
}

export async function getPaymentStatus(sessionId) {
  const res = await axios.get(`${API}/payments/status/${sessionId}`);
  return res.data;
}

export async function getPaymentHistory() {
  const res = await axios.get(`${API}/payments/history`);
  return res.data;
}

export async function getReinvestment() {
  const res = await axios.get(`${API}/payments/reinvestment`);
  return res.data;
}

export async function getPaymentPackages() {
  const res = await axios.get(`${API}/payments/packages`);
  return res.data;
}

// PayPal manual fiat withdrawal
export async function withdrawPaypal(amount) {
  const res = await axios.post(`${API}/withdraw/paypal`, null, { params: { amount } });
  return res.data;
}

export async function paypalStatus(batchId) {
  const res = await axios.get(`${API}/withdraw/paypal/status/${batchId}`);
  return res.data;
}

// Live PayPal balance (real /v1/reporting/balances; no mocks)
export async function getPayPalBalance() {
  const res = await axios.get(`${API}/paypal/balance`);
  return res.data;
}

// CoinSpot manual crypto withdrawal
export async function withdrawCoinspot({ cointype, amount, address, emailconfirm }) {
  const res = await axios.post(`${API}/withdraw/coinspot`, {
    cointype, amount, address, emailconfirm,
  });
  return res.data;
}

export async function coinspotBalances() {
  const res = await axios.get(`${API}/withdraw/coinspot/balances`);
  return res.data;
}

export async function syncCoinspot() {
  const res = await axios.post(`${API}/admin/sync-coinspot`);
  return res.data;
}

// Conversion-likelihood prediction (Cleaner LLM + optional Composio enrichment)
export async function predictFinding(findingId, opts = {}) {
  const res = await axios.post(`${API}/predict/finding/${findingId}`, opts || {});
  return res.data;
}

export async function getPredictionHistory(findingId) {
  const res = await axios.get(`${API}/predict/finding/${findingId}/history`);
  return res.data;
}

// Composio tool layer
export async function composioHealth() {
  const res = await axios.get(`${API}/composio/health`);
  return res.data;
}

export async function composioTools(toolkit, limit = 50) {
  const res = await axios.get(`${API}/composio/tools`, { params: { toolkit, limit } });
  return res.data;
}

export async function composioConnections() {
  const res = await axios.get(`${API}/composio/connections`);
  return res.data;
}

export async function composioExecute(action, args = {}) {
  const res = await axios.post(`${API}/composio/execute`, { action, arguments: args });
  return res.data;
}

// Stablecoin Yield Router (own-funds only; default-OFF, admin-gated)
export async function getYieldSettings() {
  const res = await axios.get(`${API}/yield/settings`);
  return res.data;
}

export async function updateYieldSettings(updates) {
  const res = await axios.put(`${API}/yield/settings`, updates);
  return res.data;
}

export async function getYieldStatus() {
  const res = await axios.get(`${API}/yield/status`);
  return res.data;
}

export async function runYieldCycle() {
  const res = await axios.post(`${API}/yield/run-now`);
  return res.data;
}

export async function getUserYieldConsent(userId) {
  const res = await axios.get(`${API}/users/me/yield-consent`, {
    headers: userId ? { "X-User-Id": userId } : {},
  });
  return res.data;
}

export async function updateUserYieldConsent(userId, optedIn) {
  const res = await axios.put(
    `${API}/users/me/yield-consent`,
    { opted_in: optedIn, terms_version: "v0-pending-legal" },
    { headers: { "X-User-Id": userId } }
  );
  return res.data;
}

export async function getBankSummary() {
  const res = await axios.get(`${API}/bank/summary`);
  return res.data;
}

export async function triggerBankRebate() {
  const res = await axios.post(`${API}/bank/trigger-rebate`);
  return res.data;
}

export async function getBankSettings() {
  const res = await axios.get(`${API}/bank/settings`);
  return res.data;
}

export async function updateBankSettings({ tax_rate_pct, rebate_rate_pct }) {
  const res = await axios.put(`${API}/bank/settings`, {
    tax_rate_pct,
    rebate_rate_pct,
  });
  return res.data;
}
